import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi, ltdApi } from '../services/api';

// Lifetime-deal (AppSumo) code redemption. Deliberately OUTSIDE PaywallGuard —
// a buyer whose Starter trial already expired must still be able to redeem.
const RedeemPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [status, setStatus] = useState<{ codes_redeemed: number; max_stack: number; credits_per_month: number | null } | null>(null);
  const [redeemed, setRedeemed] = useState<{ tier_name: string; credits_per_month: number | null; codes_redeemed: number; max_stack: number } | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    ltdApi.getStatus().then(setStatus).catch(() => { /* non-fatal — page still works */ });
  }, [isAuthenticated, redeemed]);

  // Signed-out buyers detour through /login; PostLoginRedirect (AppRouter)
  // reads this key after any successful login (Google, password, email link)
  // and returns them here instead of /codemate. Cleared there on use.
  useEffect(() => {
    if (!isAuthenticated) localStorage.setItem('post_login_next', '/redeem');
  }, [isAuthenticated]);

  const handleRedeem = async () => {
    setErrorMsg(null);
    setBusy(true);
    try {
      const result = await ltdApi.redeem(code.trim());
      setRedeemed(result);
      setCode('');
      // Refresh the cached user so the sidebar shows the new plan immediately
      // (same pattern as the post-payment flow on /pay).
      try {
        const me = await authApi.validateToken();
        if (me) {
          localStorage.setItem('user', JSON.stringify(me));
          window.dispatchEvent(new CustomEvent('auth-token-saved', { detail: { user: me } }));
        }
      } catch { /* best-effort only */ }
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.detail || err?.message || 'Could not redeem the code. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 px-4 py-12">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">Redeem your lifetime code</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-3">
            Bought a Cubemate lifetime deal? Enter your code below to activate lifetime access.
            Stack up to 5 codes for more monthly credits.
          </p>
        </div>

        {!isAuthenticated ? (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 text-center">
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              First create a free account (or sign in) — we&apos;ll bring you right back here to enter your code.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm"
            >
              Sign in / Create account
            </button>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Signed in as <span className="font-medium">{user?.email}</span>
              {status && status.codes_redeemed > 0 && (
                <> · {status.codes_redeemed}/{status.max_stack} codes redeemed
                  {status.credits_per_month ? ` — ${status.credits_per_month.toLocaleString()} credits/month` : ''}</>
              )}
            </p>

            {redeemed ? (
              <div className="text-center py-4">
                <div className="text-4xl mb-3">🎉</div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                  {redeemed.tier_name} activated
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                  {redeemed.credits_per_month?.toLocaleString()} credits every month, forever.
                  {redeemed.codes_redeemed < redeemed.max_stack && (
                    <> Have another code? Stack it below for a bigger monthly allowance.</>
                  )}
                </p>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => navigate('/codemate')}
                    className="px-6 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm"
                  >
                    Go to workspace →
                  </button>
                  {redeemed.codes_redeemed < redeemed.max_stack && (
                    <button
                      onClick={() => setRedeemed(null)}
                      className="px-6 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 font-semibold text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      Redeem another code
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your code
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={e => setCode(e.target.value.toUpperCase())}
                  onKeyDown={e => { if (e.key === 'Enter' && code.trim() && !busy) handleRedeem(); }}
                  placeholder="CM-XXXX-XXXX-XXXX"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-white font-mono tracking-wider placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 mb-4"
                  autoFocus
                />
                {errorMsg && (
                  <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-800 dark:text-red-200">
                    {errorMsg}
                  </div>
                )}
                <button
                  onClick={handleRedeem}
                  disabled={!code.trim() || busy}
                  className="w-full px-6 py-3 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold"
                >
                  {busy ? 'Activating…' : 'Redeem code'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RedeemPage;
